import { useEffect, useState, type MouseEvent } from 'react';
import { LikeButton } from '@/components/LikeButton';
import { MapButtons } from '@/components/MapButtons';
import { copyText } from '@/lib/clipboard';
import type { Studio } from '@/types';

interface StudioRowProps {
  studio: Studio;
  onOpenStudio?: (studio: Studio) => void;
  aiIdPrefix?: string;
}

type CopyStatus = 'idle' | 'copied' | 'failed';
const INTERACTIVE_ROW_TARGET_SELECTOR = 'a, button, input, textarea, select, option, label, [role="button"], [role="link"]';

function isInteractiveRowTarget(target: EventTarget | null, rowElement: HTMLElement) {
  if (!(target instanceof Element)) {
    return false;
  }

  const interactiveElement = target.closest(INTERACTIVE_ROW_TARGET_SELECTOR);
  return interactiveElement !== null && rowElement.contains(interactiveElement);
}

export function StudioRow({ studio, onOpenStudio, aiIdPrefix = 'studio-list-item' }: StudioRowProps) {
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle');
  const hasLocation = studio.location.trim().length > 0;

  useEffect(() => {
    if (copyStatus === 'idle') {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setCopyStatus('idle'), 1400);
    return () => window.clearTimeout(timeoutId);
  }, [copyStatus]);

  async function handleCopyLocation() {
    if (!hasLocation) {
      setCopyStatus('failed');
      return;
    }

    try {
      const didCopy = await copyText(studio.location);
      setCopyStatus(didCopy ? 'copied' : 'failed');
    } catch {
      setCopyStatus('failed');
    }
  }

  const copyLabel = copyStatus === 'copied' ? '복사됨' : copyStatus === 'failed' ? '실패' : '주소복사';
  const copyButtonClassName =
    copyStatus === 'copied'
      ? 'inline-flex h-9 shrink-0 items-center justify-center rounded-lg border border-emerald-300 bg-emerald-100 px-2.5 text-sm font-semibold text-emerald-800 shadow-sm shadow-emerald-100 transition hover:bg-emerald-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald-100'
      : 'inline-flex h-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 px-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-slate-100';
  const spaceCloudUrl = studio.spaceCloudUrl.trim();
  const adminContact = studio.adminContact.trim();
  const adminContactHref = adminContact ? `tel:${adminContact.replace(/[^\d+]/g, '') || adminContact}` : '';
  const openStudio = () => onOpenStudio?.(studio);

  function handleRowClick(event: MouseEvent<HTMLLIElement>) {
    if (isInteractiveRowTarget(event.target, event.currentTarget)) {
      return;
    }

    openStudio();
  }

  return (
    <li className="cursor-pointer px-4 py-3 transition hover:bg-slate-50" onClick={handleRowClick} data-ai-id={aiIdPrefix}>
      <div className="space-y-2" data-ai-id={`${aiIdPrefix}-content`}>
        <div className="flex min-w-0 items-baseline gap-x-3" data-ai-id={`${aiIdPrefix}-summary`}>
          <button
            type="button"
            onClick={openStudio}
            className="min-w-0 max-w-[56%] shrink-0 break-keep text-left text-base font-bold text-slate-950 transition hover:text-emerald-700 focus:outline-none focus-visible:text-emerald-700"
            data-ai-id={`${aiIdPrefix}-summary-title-button`}
          >
            <span className="block truncate" data-ai-id={`${aiIdPrefix}-summary-title-button-label-text`}>
              {studio.title}
            </span>
          </button>
          <p className="min-w-0 flex-1 text-sm leading-5 text-slate-500" data-ai-id={`${aiIdPrefix}-summary-location`}>
            <span className="block truncate" data-ai-id={`${aiIdPrefix}-summary-location-text`}>
              {studio.location || '위치 미입력'}
            </span>
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-1.5" data-ai-id={`${aiIdPrefix}-primary-actions`}>
          <button
            type="button"
            onClick={openStudio}
            className="inline-flex h-9 shrink-0 items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100 hover:text-emerald-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald-100"
            aria-label={`${studio.title} 상세보기`}
            data-ai-id={`${aiIdPrefix}-primary-actions-detail-button`}
          >
            <span data-ai-id={`${aiIdPrefix}-primary-actions-detail-button-label-text`}>상세보기</span>
          </button>
          <button
            type="button"
            onClick={handleCopyLocation}
            disabled={!hasLocation}
            className={copyButtonClassName}
            data-ai-id={`${aiIdPrefix}-primary-actions-copy-location-button`}
          >
            <span data-ai-id={`${aiIdPrefix}-primary-actions-copy-location-button-label-text`}>{copyLabel}</span>
          </button>
          <MapButtons
            kakaoMapUrl={studio.kakaoMapUrl}
            naverMapUrl={studio.naverMapUrl}
            studioTitle={studio.title}
            aiIdPrefix={`${aiIdPrefix}-map-buttons`}
            className="contents"
          />
          {spaceCloudUrl ? (
            <a
              href={spaceCloudUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-9 shrink-0 items-center justify-center rounded-lg border border-slate-950 bg-slate-950 px-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-slate-200"
              data-ai-id={`${aiIdPrefix}-primary-actions-spacecloud-link`}
            >
              <span data-ai-id={`${aiIdPrefix}-primary-actions-spacecloud-link-label-text`}>예약하기</span>
            </a>
          ) : (
            <button
              type="button"
              disabled
              className="inline-flex h-9 shrink-0 cursor-not-allowed items-center justify-center rounded-lg border border-slate-200 bg-slate-50 px-2.5 text-sm font-semibold text-slate-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-slate-100"
              title="스페이스클라우드 링크가 아직 입력되지 않았어요."
              aria-label={`${studio.title} 예약 링크 미입력`}
              data-ai-id={`${aiIdPrefix}-primary-actions-spacecloud-link`}
            >
              <span data-ai-id={`${aiIdPrefix}-primary-actions-spacecloud-link-label-text`}>예약하기</span>
            </button>
          )}
          {adminContact ? (
            <a
              href={adminContactHref}
              className="inline-flex h-9 shrink-0 items-center justify-center rounded-lg border border-sky-200 bg-sky-50 px-2.5 text-sm font-semibold text-sky-700 transition hover:bg-sky-100 hover:text-sky-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-sky-100"
              aria-label={`${studio.title} 관리자 연락처로 전화하기`}
              data-ai-id={`${aiIdPrefix}-primary-actions-admin-contact-link`}
            >
              <span data-ai-id={`${aiIdPrefix}-primary-actions-admin-contact-link-label-text`}>관리자연락처</span>
            </a>
          ) : (
            <button
              type="button"
              disabled
              className="inline-flex h-9 shrink-0 cursor-not-allowed items-center justify-center rounded-lg border border-slate-200 bg-slate-50 px-2.5 text-sm font-semibold text-slate-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-slate-100"
              title="관리자 연락처가 아직 입력되지 않았어요."
              aria-label={`${studio.title} 관리자 연락처 미입력`}
              data-ai-id={`${aiIdPrefix}-primary-actions-admin-contact-link`}
            >
              <span data-ai-id={`${aiIdPrefix}-primary-actions-admin-contact-link-label-text`}>관리자연락처</span>
            </button>
          )}
          <LikeButton studioId={studio.id} initialCount={studio.likeCount} aiIdPrefix={`${aiIdPrefix}-primary-actions-like-button`} />
        </div>
      </div>
    </li>
  );
}
